import JSON5 from 'json5'

export default class ReadingCoachReadalong {
  // Offset used to shift when a word begins to highlight
  static startOffset = -0.2

  // Offset used to shift when a word ends highlighting
  static endOffset = 0.1

  constructor (modal) {
    this.modal = modal
    this.radioButtons = [...this.modal.querySelectorAll('.js-audio-toggle-button')]
    this.playerWrappers = [...this.modal.querySelectorAll('.js-player-wrapper')]
    this.readalongText = this.modal.querySelector('.js-readalong-text')
    this.readalongA11y = this.modal.querySelector('.js-readalong-a11y')
    this.readalongCont = this.modal.querySelector('.js-readalong-cont')
    this.readalongLang = this.modal.querySelector('.js-readalong-lang')
    this.readalongHeadline = this.modal.querySelector('.js-translatable-headline')
    this.readalongSubhead = this.modal.querySelector('.js-translatable-subhead')
    this.player = undefined
    this.spans = undefined

    if (!window.rccData || !window.rccData[this.modal.id]) return

    this.data = window.rccData[this.modal.id]
    this.activeData = this.data[0]

    this.radioButtons.forEach(button => {
      button.addEventListener('change', event => {
        this.activeData = this.data.find(item => item.key === event.target.value)

        // Resets all of audio elements on the page
        if (
          'audioplayers' in window.unlocked &&
          window.unlocked.audioplayers !== null &&
          window.unlocked.audioplayers !== undefined
        ) {
          Object.entries(window.unlocked.audioplayers).forEach(([key, player]) => {
            player.reset()
          })
        }

        this.setCurrentPlayer()
        this.showHideAudio()
        this.updateForData()
      })
    })

    // No open event for HTMLDialogElement???
    // Can't run this on init because ReadingCoachReadalong might initialize
    // before the audioplayer list is ready.
    const modalOpenObserver = new MutationObserver((event) => {
      if (event[0].attributeName === 'open') {
        if (event[0].target.open) {
          this.setCurrentPlayer()
          this.showHideAudio()
          this.updateForData()
        }
      }
    })
    modalOpenObserver.observe(this.modal, { attributes: true })

    this.updateForData()
  }

  /**
   * Sets the current player object
   */
  setCurrentPlayer = () => {
    if (this.radioButtons.length > 1) {
      this.player = this.getPlayerObject(this.modal.querySelector('.js-audio-toggle-button:checked').value)
    } else if (this.radioButtons.length === 1) {
      this.player = this.getPlayerObject()
    }
  }

  /**
   * Reads the JSON at the give path and returns the parsed result
   * @param {string} path
   */
  readJSON = (path) => {
    if (!path) {
      return false
    }

    const request = new XMLHttpRequest()
    request.open('GET', path, false)
    request.send()

    return JSON5.parse(request.responseText)
  }

  /**
   * Updates the readalong text when the source changes
   */
  updateForData = () => {
    this.player?.removeEventListener('play', this.fadeReadalong)
    this.player?.removeEventListener('ended', this.refreshReadalong)
    this.player?.removeEventListener('timeupdate', this.highlightText)
    this.player?.removeEventListener('seeked', this.seekHighlightText)

    // Reference to the underlying <audio> element
    this.player = this.getPlayerObject(event.target.value)

    if (this.readalongText && this.readalongA11y) {
      const transcript = this.readJSON(this.activeData.transcript)
      if (transcript) {
        this.readalongText.replaceChildren(this.splitTranscriptWords(transcript))
        this.readalongA11y.innerHTML = `${transcript.text}`
      } else {
        this.readalongText.innerHTML = ''
        this.readalongA11y.innerHTML = ''
      }
    }

    if (this.readalongCont) {
      this.readalongCont.innerHTML = this.activeData.continued
    }

    if (this.readalongLang) {
      this.readalongLang.lang = this.activeData.language
    }

    if (this.readalongHeadline) {
      if (this.activeData.headline) {
        this.readalongHeadline.innerHTML = this.activeData.headline
        this.readalongHeadline.lang = this.activeData.language
      } else {
        this.readalongHeadline.innerHTML = this.readalongHeadline.dataset.defaultHeadline
        this.readalongHeadline.removeAttribute('lang')
      }
    }

    if (this.readalongSubhead) {
      if (this.activeData.subhead) {
        this.readalongSubhead.innerHTML = this.activeData.subhead
        this.readalongSubhead.lang = this.activeData.language
      } else {
        this.readalongSubhead.innerHTML = this.readalongSubhead.dataset.defaultSubhead
        this.readalongSubhead.removeAttribute('lang')
      }
    }

    this.spans = [...this.readalongText.querySelectorAll('span')]

    this.player?.addEventListener('play', this.fadeReadalong)
    this.player?.addEventListener('ended', this.refreshReadalong)
    this.player?.addEventListener('timeupdate', this.highlightText)
    this.player?.addEventListener('seeked', this.seekHighlightText)
  }

  /**
   * Sets the appearance of the readalong text to highlight word-by-word
   */
  fadeReadalong = () => {
    this.readalongText.classList.add('playing')
  }

  /**
   * Resets the readalong text to default appearance
   */
  refreshReadalong = () => {
    this.readalongText.classList.remove('playing')
  }

  /**
   * Highlights text for the current audio timestamp.
   */
  highlightText = (skipBreaks) => {
    if (!this.player) return

    const currentTime = this.player.currentTime

    // Traditional for loops are *ever so slightly* faster then forEach. This is
    // doing a lot of DOM manipulation in a short timeframe so every ms counts.
    /* eslint no-labels: ["error", { "allowLoop": true }] */
    spanLoop: for (let i = 0; i < this.spans.length; i++) {
      const span = this.spans[i]
      const { start, end } = span.dataset
      let afterHighlight = false
      if (
        currentTime >= Number(start) + ReadingCoachReadalong.startOffset &&
        currentTime <= Number(end) + ReadingCoachReadalong.endOffset
      ) {
        span.classList.add('highlight')
        afterHighlight = true
      } else {
        if (skipBreaks !== true && afterHighlight === true) {
          break spanLoop
        }
        span.classList.remove('highlight')
      }
    }
  }

  seekHighlightText = () => {
    this.highlightText(true)
  }

  /**
   * Transforms a raw transcript into one with each word wrapped in a span with
   * data attributes for start and end times.
   *
   * @param {Object} transcript - An object containing the full transcript along with an array of words with timestamps
   */
  splitTranscriptWords = (transcript) => {
    let fullText = transcript.text
    if (!Array.isArray(transcript.text)) {
      fullText = [
        transcript.text
      ]
    }
    let wordsSoFar = ''

    let paragraphIndex = 0
    transcript.words.forEach(({ word, start }, index) => {
      let end = transcript.duration
      if (index < transcript.words.length - 1) {
        end = transcript.words[index + 1].start
      }

      let regex = new RegExp(`(${wordsSoFar}.*?)(${word})`)
      const attrs = `data-start="${start}" data-end="${end}"`
      const replacement = `<span ${attrs}>${word}</span>`

      if (fullText[paragraphIndex].match(regex, `$1${replacement}`)) {
        fullText[paragraphIndex] = fullText[paragraphIndex].replace(regex, `$1${replacement}`)
        wordsSoFar = `${wordsSoFar}.*?${replacement}`
      } else {
        paragraphIndex += 1
        wordsSoFar = ''
        regex = new RegExp(`(${wordsSoFar}.*?)(${word})`)
        fullText[paragraphIndex] = fullText[paragraphIndex].replace(regex, `$1${replacement}`)
        wordsSoFar = `${wordsSoFar}.*?${replacement}`
      }
    })

    const div = document.createElement('div')
    fullText.forEach(text => {
      const p = document.createElement('p')
      p.innerHTML = text
      div.appendChild(p)
    })

    return div
  }

  /**
   * Shows or hides the appropriate audio player
   */
  showHideAudio = () => {
    this.playerWrappers.forEach(wrapper => {
      if (!this.player) {
        wrapper.classList.add('hidden')
      } else if (this.player.closest('.js-player-wrapper') === wrapper) {
        wrapper.classList.remove('hidden')
      } else {
        wrapper.classList.add('hidden')
      }
    })

    this.refreshReadalong()
  }

  /**
   * Returns a reference to the globally stored audio player object
   * @param {string} key - Unique identifier used to find the correct audio player instance
   */
  getPlayerObject = (key) => {
    let selector = '[data-audio-id] .js-audio-player'
    if (key) {
      selector = `[data-audio-id="${key}"] .js-audio-player`
    }

    const player = this.modal.querySelector(selector)
    if (player) {
      const id = this.modal.querySelector(selector).id
      return window.unlocked.audioplayers[id].htmlPlayer
    }

    return undefined
  }
}
